import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, CircularProgress, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, InputAdornment, Stack, TextField, Typography, } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
// icons
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from "common/customIcons/search";
// utils and helpers
import { debounce } from "utilities/common";
import { addTemplateKitProducts, getAssignedProducts, getTemplateKitDetails } from "../../../redux/slices/hyperwolf/inventoryDistribution";
import { openSnackbar } from '../../../redux/slices/common';
import { closeCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import { getCategoryProduct } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import MDButton from 'components/MDButton';
import InfiniteScrollTable from "components/tables/infiniteScrollTable";

export default function AssignProductsModal({ closeModal }) {
  const dispatch = useDispatch()

  const { modalData, isLoading = false } = useSelector(state => state.modal);
  const { templateId, setRefreshTable: setRefreshParentTable } = modalData;

  const [inputFilters, setInputFilters] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [details, setDetails] = useState({})
  const [refreshTable, setRefreshTable] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectionSource, setSelectionSource] = useState("parent");

  const showError = (message, errorType) => {
    if (errorType === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errorType === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const handleSave = async () => {
    try {
      if (isLoading || !selectedProducts?.length) return;

      dispatch(setModalLoader(true));
      const response = await dispatch(addTemplateKitProducts({ templateId, products: selectedProducts })).unwrap();
      setRefreshParentTable(prev => !prev)

      dispatch(closeCustomModalDialog());
    } catch (err) {
      dispatch(setModalLoader(false));
      showError(err?.message, 'error');
    };
  }

  const handleTemplateKitDetails = async (filters = {}) => {
    try {
      const response = await dispatch(getTemplateKitDetails({ templateId, ...customFilters, ...filters })).unwrap()
      if (response?.data) {
        setDetails(response?.data)
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleAssignedProducts = async (filters = {}) => {
    try {
      const request = { templateId, ...customFilters, ...filters }
      const response = await dispatch(getAssignedProducts(request)).unwrap()
      const data = response?.data
      if (data) {
        setSelectionSource("parent")

        const productIds = data?.map(product => product?._id) || [];
        const categoryIndex = selectedProducts.findIndex((row) => row.boxId === request?.boxId);

        setSelectedProducts(prev => {
          let newProducts = [...prev];

          if (categoryIndex >= 0) {
            newProducts[categoryIndex] = { ...newProducts[categoryIndex], productIds, isAllSelected: false };
          } else {
            newProducts = [...newProducts, { boxId: request?.boxId, productIds, isAllSelected: false }];
          }
          return newProducts
        })
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleDebounce = useCallback(debounce((event) => {
    const { name, value } = event.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleSelectAll = (e) => {
    const { name, checked } = e.target;
    const categoryIndex = selectedProducts?.findIndex(item => item?.boxId === name);

    setSelectionSource("parent");

    if (categoryIndex >= 0) {
      setSelectedProducts(prev => {
        const newProducts = [...prev]
        newProducts[categoryIndex] = { ...newProducts[categoryIndex], isAllSelected: checked, productIds: [] }
        return newProducts
      })
    } else {
      setSelectedProducts(prev => {
        const newProducts = [...prev, { boxId: name, isAllSelected: checked, productIds: [] }]
        return newProducts
      })
    }
  };

  const handleGetSelectedProducts = (selection) => {
    setSelectedProducts(selection)
  }

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Product Name/SKU',
        field: 'adminProductName',
        suppressMenu: false,
        minWidth: 350,
        sortable: false,
        checkboxSelection: true,
        selectedAll: true,
        setRowId: ({ data }) => data?.productId,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { adminProductName, sku, assets } = params?.data || {};
          const { thumbURL, name } = assets?.[0] || {}

          return <Box style={{ display: "flex", gap: "10px" }}>
            <img src={thumbURL} alt={name} width={30} height={30} />
            <Box>
              <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px" }}>{adminProductName}</Typography>
              {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px" }}>{sku}</Typography>}
            </Box>
          </Box>
        }
      },
      {
        headerName: 'Category',
        field: 'category',
        suppressMenu: false,
        minWidth: 200,
        sortable: false,
        valueGetter: ({ data }) => data?.category?.name || "-",
      },
      {
        headerName: 'Brand',
        field: 'brand',
        suppressMenu: false,
        minWidth: 200,
        sortable: false,
        valueGetter: ({ data }) => data?.brand?.name || "-",
      },
      {
        headerName: 'Price per piece',
        field: 'pricePerPiece',
        suppressMenu: false,
        sortable: false,
        minWidth: 150,
        valueGetter: ({ data }) => {
          return data?.pricePerPiece ? `$${data?.pricePerPiece}` : "-"
        },
        headerClass: 'jc-start',
      },
    ]
  }, []);

  useEffect(() => {
    if (templateId) {
      handleTemplateKitDetails()
    }
  }, [templateId])

  return (
    <>
      <DialogTitle id='draggable-dialog-title' color='#9A9AAF' display='flex' alignItems='center' justifyContent='space-between'>
        {"Add SKU'S"}
        <IconButton onClick={closeModal} edge='end' size='small'>
          <CloseIcon fontSize='medium' />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>

          <Box className="ag-grid-table-white">
            <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>

              {/* Search filters */}
              <Grid item xs={12}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ sm: "center" }}
                  justifyContent="end"
                  mt={1}
                >
                  <Box className="search-gray">
                    <TextField
                      className="custom-search-field"
                      placeholder="Search"
                      label=""
                      InputProps={{
                        startAdornment: <InputAdornment position="start">
                          <IconButton size='small'>
                            <SearchIcon width='18px' height='18px' />
                          </IconButton>
                        </InputAdornment>
                      }}
                      name="search"
                      value={inputFilters.search}
                      onChange={event => {
                        const { value } = event.target;
                        setInputFilters({ ...inputFilters, search: value })
                        handleDebounce(event)
                      }}
                      style={{ minWidth: "300px" }}
                    />
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                {/* Boxes listing */}
                <Box style={{ width: "100%" }}>
                  {/* <Box className="form-check px-3 pb-2 d-flex align-item-center gap-2 text-14">
                    <input
                      className="form-check-input cur-pointer"
                      type="checkbox"
                      id={`flexCheckIndeterminate${boxId}`}
                      name={boxId}
                      checked={isSelectedAll}
                      onChange={handleSelectAll}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`flexCheckIndeterminate${boxId}`}
                    >
                      Select All
                    </label>
                  </Box> */}
                  <div className="cus-four-columns">
                    <div className="ag-theme-quartz" style={{ height: "450px", width: "100%" }}>
                      <InfiniteScrollTable
                        id="infiniteScrollModalTable"
                        columnDefs={columnDefs}
                        getDataMethod={getCategoryProduct}
                        refreshTable={refreshTable}
                        customFilters={{ ...customFilters, templateId }}
                        onSelectionChange={handleGetSelectedProducts}
                      // isSelectedAll={isSelectedAll}
                      // selectedProducts={selectedProducts}
                      // setSelectedProducts={setSelectedProducts}
                      // selectionSource={selectionSource}
                      // setSelectionSource={setSelectionSource}
                      />
                    </div>
                  </div>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContentText>
      </DialogContent>

      {/* Action buttons */}
      <DialogActions className='footer-modal' style={{ padding: "16px", justifyContent: "start", boxShadow: "0 -5px 4px 0 rgba(213, 220, 227, 0.25)" }}>
        <MDButton
          onClick={handleSave}
          sx={{ minWidth: "160px", height: "44px" }}
          type="button"
          variant="contained"
          color="primary"
          disabled={!selectedProducts?.length}
        >
          {isLoading ? <CircularProgress size={16} color='light' /> : ' Save'}
        </MDButton>
        <MDButton
          onClick={closeModal}
          sx={{ minWidth: "160px", height: "44px" }}
          type="button"
          variant="outlined"
          color="dark"
        >
          Cancel
        </MDButton>
      </DialogActions>
    </>
  );
}